import { useState, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { Phone, Mail, Check } from 'lucide-react'
import { contactCTAs } from './config'
import type { ChatMessageProps, ContactCTACopy } from '@brochure/shared'

/**
 * Parse `**bold**` markdown into React nodes. Keeps the rest of the text as-is.
 */
function parseMarkdownBold(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={index} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      )
    }
    return part
  })
}

const ctaIcons: Record<string, React.ReactNode> = {
  phone: <Phone className="w-3 h-3" strokeWidth={1.5} />,
  email: <Mail className="w-3 h-3" strokeWidth={1.5} />,
}

const ctaBase =
  'inline-flex items-center gap-1.5 px-2.5 py-1 text-[10.5px] ' +
  'transition-colors duration-[var(--duration-fast)] uppercase ' +
  'border'

const ctaIdle =
  'text-[var(--color-muted)] border-[var(--color-border)] ' +
  'hover:text-[var(--color-fg)] hover:border-[var(--color-fg)] ' +
  'hover:bg-[var(--color-bg)]'

function CopyButton({
  cta,
  label,
  copiedLabel,
}: {
  cta: ContactCTACopy
  label: string
  copiedLabel: string
}) {
  const [copied, setCopied] = useState(false)

  const handleClick = () => {
    navigator.clipboard.writeText(cta.value)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      onClick={handleClick}
      className={cn(
        ctaBase,
        copied
          ? 'text-[var(--color-fg)] border-[var(--color-highlight)] bg-[var(--color-highlight)]/15'
          : ctaIdle
      )}
      style={{
        borderRadius: 'var(--radius-sm)',
        letterSpacing: '0.12em',
        fontFamily: 'var(--font-body)',
        fontWeight: 500,
      }}
    >
      {copied ? <Check className="w-3 h-3" strokeWidth={1.5} /> : ctaIcons[cta.id]}
      <span>{copied ? copiedLabel : label}</span>
    </button>
  )
}

export function ChatMessage({ message, isTyping = false }: ChatMessageProps) {
  const { t } = useTranslation('common')
  const isUser = message.role === 'user'

  const formattedContent = useMemo(() => {
    if (!message.content) return null
    return parseMarkdownBold(message.content)
  }, [message.content])

  return (
    <div className={cn('flex flex-col', isUser ? 'items-end' : 'items-start')}>
      <div
        className={cn(
          'max-w-[88%] px-4 py-3 relative',
          isUser
            ? 'bg-[var(--color-accent)] text-[var(--color-on-accent)]'
            : 'bg-[var(--color-surface)] text-[var(--color-fg)] border border-[var(--color-border)]'
        )}
        style={{
          borderRadius: 'var(--radius-sm)',
          fontFamily: 'var(--font-body)',
        }}
      >
        {/* Champagne hairline marker on assistant — left edge accent, editorial mark */}
        {!isUser && !isTyping && (
          <span
            aria-hidden="true"
            className="absolute top-3 bottom-3 left-0 w-px"
            style={{
              background: 'var(--color-highlight)',
              transform: 'translateX(-1px)',
              opacity: 0.7,
            }}
          />
        )}

        {isTyping ? (
          <div className="flex gap-1.5 py-1.5 px-1 items-center">
            <span
              className="w-1.5 h-1.5 rounded-full animate-bounce"
              style={{ background: 'var(--color-muted)', animationDelay: '0ms' }}
            />
            <span
              className="w-1.5 h-1.5 rounded-full animate-bounce"
              style={{ background: 'var(--color-muted)', animationDelay: '150ms' }}
            />
            <span
              className="w-1.5 h-1.5 rounded-full animate-bounce"
              style={{ background: 'var(--color-muted)', animationDelay: '300ms' }}
            />
          </div>
        ) : (
          <p
            className="text-[0.875rem] whitespace-pre-wrap"
            style={{ lineHeight: 1.6 }}
          >
            {formattedContent}
          </p>
        )}
      </div>

      {/* Contact CTAs — only for assistant messages, not typing */}
      {!isUser && !isTyping && message.content && contactCTAs.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mt-2.5 ml-px">
          {contactCTAs.map((cta) =>
            cta.kind === 'copy' ? (
              <CopyButton
                key={cta.id}
                cta={cta}
                label={t(cta.labelKey)}
                copiedLabel={t('chatbot.cta.copied')}
              />
            ) : (
              <a
                key={cta.id}
                href={cta.url}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(ctaBase, ctaIdle)}
                style={{
                  borderRadius: 'var(--radius-sm)',
                  letterSpacing: '0.12em',
                  fontFamily: 'var(--font-body)',
                  fontWeight: 500,
                }}
              >
                {cta.icon && <img src={cta.icon} alt="" className="w-3 h-3" />}
                <span>{t(cta.labelKey)}</span>
              </a>
            )
          )}
        </div>
      )}
    </div>
  )
}
